/**
 * Creates a memoized version of a function.
 * The cache key is generated by JSON.stringify(args).
 * This works well for arguments that are JSON-serializable primitives,
 * objects, or arrays. It may not work as expected for complex objects,
 * functions, Symbols, or circular references as arguments.
 *
 * @template A The type of the arguments array for the function.
 * @template R The return type of the function.
 * @param {function(...args: A): R} fn The function to memoize.
 * @returns {function(...args: A): R} The memoized function.
 */
export function memoize<A extends any[], R>(fn: (...args: A) => R): (...args: A) => R {
    // Use a Map to store cached results. Key: stringified arguments, Value: result.
    const cache = new Map<string, R>();

    // Return the memoized function
    return (...args: A): R => {
        // Create a cache key by stringifying the arguments array
        const key = JSON.stringify(args);

        // Check if the result for these arguments is already in the cache
        if (cache.has(key)) {
            // console.log('Cache HIT for key:', key); // Optional: for debugging/demonstration
            // Return the cached result. The non-null assertion (!) is safe
            // because we just checked cache.has(key).
            return cache.get(key)!;
        }

        // console.log('Cache MISS for key:', key); // Optional: for debugging/demonstration
        // If not in cache, call the original function with the arguments
        const result = fn(...args);

        // Store the new result in the cache
        cache.set(key, result);

        // Return the newly computed result
        return result;
    };
}
